import some from 'lodash/some';

import { AccountNotificationFragment } from '~/generated/graphql';

const ACCOUNT_CONNECTOR_TYPES = [
  'account_connector_auth',
  'account_connector_confirm_user',
  'account_connector_terms',
  'salesforce_user_api_disabled',
];

const ENRICHMENT_TYPES = ['account_connector_enrichment_error'];

const DESTINATION_TYPES = [
  'destination_archived_slack_channel',
  'google_conversion_action_not_found',
];

type NotificationTypes = AccountNotificationFragment['notificationType'][number];

const documentationLinks = {
  account_connector_terms:
    'https://help.clearbit.com/hc/en-us/articles/360043723133-Troubleshooting-Facebook-Ads',
  salesforce_user_api_disabled:
    'https://help.clearbit.com/hc/en-us/articles/360056896793-Troubleshooting-Salesforce-Syncs',
  salesforce_sync_error:
    'https://help.clearbit.com/hc/en-us/articles/360056896793-Troubleshooting-Salesforce-Syncs',
};

const isEnrichment = (notificationType: NotificationTypes) =>
  ENRICHMENT_TYPES.some((type) => notificationType === type);

export function buildAccountNotification(
  message: string | undefined | null,
  notificationType: NotificationTypes,
  readableSourceName = 'Salesforce',
) {
  const isEnrichmentNotification = isEnrichment(notificationType);
  const pickType = (types: NotificationTypes[]) =>
    types.some((type) => notificationType == type);
  const documentationLink =
    documentationLinks[notificationType as keyof typeof documentationLinks];
  const messageDescription = !isEnrichmentNotification ? message : null;
  const messageTitle = isEnrichmentNotification
    ? `Clearbit was unable to update your fields in ${readableSourceName}.`
    : null;

  const buildHelpText = () => {
    return documentationLink ? 'Read the docs' : 'Fix this issue';
  };

  return {
    shouldRenderNotification: (isFromEnrichmentPage: boolean) => {
      return isFromEnrichmentPage
        ? isEnrichmentNotification
        : !isEnrichmentNotification;
    },
    isEnrichmentNotification,
    isAccountConnectorNotification: pickType(ACCOUNT_CONNECTOR_TYPES),
    isDestinationNotification: pickType(DESTINATION_TYPES),
    documentationLink,
    helpText: buildHelpText(),
    messageDescription,
    messageTitle,
  };
}

type OptionsType = {
  isFromEnrichmentPage?: boolean | undefined;
};

export function hasNotifications(
  notifications: AccountNotificationFragment[],
  options: OptionsType = {},
) {
  if (!some(notifications)) {
    return false;
  }

  if (options.isFromEnrichmentPage) {
    return some(notifications, (notification) =>
      isEnrichment(notification.notificationType),
    );
  }

  const notificationsWithoutEnrichment = notifications.filter(
    (notification) => !isEnrichment(notification.notificationType),
  );

  return some(notificationsWithoutEnrichment);
}
